<template>
  <div
    v-if="isLoading"
    class="bg-static-default-mid rounded-md min-h-72 animate-pulse p-16 motion-reduce:animate-none"
    data-test="loading-container"
  />

  <template v-else>
    <div
      ref="root"
      class="bg-static-warning-mid rounded-md flex flex-col items-center gap-8 p-16 md:flex-row"
    >
      <p class="text-static-default-hi body-2">
        {{ i18n(translations.explanation) }}
      </p>

      <RevButton
        class="shrink-0"
        full-width="adaptive"
        variant="primary"
        @click="onProductPageLinkCLick"
      >
        {{ i18n(translations.button) }}
      </RevButton>
    </div>
  </template>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale, useRouter } from '#imports'
import { onUnmounted, useTemplateRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevButton } from '@ds/components/Button'
import { useIntersectionObserver } from '@vueuse/core'

import { PRODUCT } from '~/scopes/product/route-names'

import translations from './OutOfStock.translations'

const props = defineProps<{
  item: CartItem
  isLoading: boolean
}>()

const emit = defineEmits<{
  remove: []
}>()

const i18n = useI18n()
const tracking = useTracking()
const router = useRouter()
const injectLocale = useRouteLocationWithLocale()
const root = useTemplateRef('root')

async function onProductPageLinkCLick() {
  tracking.trackClick({
    name: 'see_price',
    zone: 'cart',
  })

  const locationWithLocale = injectLocale({
    type: 'internal',
    name: PRODUCT.HOME,
    params: {
      slugV2: props.item.link.params.slugV2,
      uuid: props.item.link.params.uuid,
    },
    query: removeEmptyValuesInObject({
      // When the item is out of stock, we have the legacy grade ids (1,2,3,4,5), which causes
      // an error when arriving on the product page. We need to fix the cart endpoint or have a custom
      // mapping here. In the meantime, we're just ignoring it (the PP will default to the cheapest grade)
      // l: props.item.gradeId,
      offerType: props.item.gradeExtended?.hasNewBattery ? 7 : undefined,
    }),
  })

  // Type assertion to bypass the hash property type mismatch
  await router.push(locationWithLocale as RouteLocationRaw)

  emit('remove')
}

const { stop } = useIntersectionObserver(
  root,
  ([element], observer) => {
    if (element.isIntersecting) {
      tracking.trackBlockImpression({
        name: 'cart_item_no_alternative',
        product_category: props.item.category,
        product_model: props.item.model,
        product_id: props.item.akeneoId,
        grade: props.item.gradeId,
        offer_type: props.item.gradeExtended?.hasNewBattery ? 7 : 0,
      })

      observer.unobserve(element.target)
    }
  },
  {},
)

onUnmounted(() => {
  stop()
})
</script>
